import React, { createContext, useContext, useState, useEffect } from "react";
import ApiService from "../services/ApiService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem("authToken");
    const userRole = localStorage.getItem("userRole");
    const userData = localStorage.getItem("userData");

    if (token && userRole) {
      try {
        const parsedUserData = userData ? JSON.parse(userData) : {};
        setUser({
          token,
          role: userRole,
          ...parsedUserData,
        });
      } catch (error) {
        console.error("Error parsing user data:", error);
        // Clear corrupted data
        localStorage.removeItem("authToken");
        localStorage.removeItem("userRole");
        localStorage.removeItem("userData");
      }
    }
    setLoading(false);
  }, []);

  // Listen for storage changes (logout from another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "authToken" && !e.newValue) {
        // Token was removed, logout user
        setUser(null);
      }
    };

    const handleAuthError = () => {
      // Handle forced logout from API errors
      setUser(null);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("authError", handleAuthError);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("authError", handleAuthError);
    };
  }, []);

  const login = (userData) => {
    localStorage.setItem("authToken", userData.token);
    localStorage.setItem("userRole", userData.role);
    localStorage.setItem("userData", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = async () => {
    try {
      await ApiService.logout();
    } catch (error) {
      console.warn(
        "Logout API call failed, but continuing with local logout:",
        error
      );
    }
    setUser(null);
  };

  const forceLogout = () => {
    // Force logout without API call (used for auth errors)
    localStorage.removeItem("authToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userData");
    setUser(null);
  };

  const isAuthenticated = () => {
    return !!user && !!user.token;
  };

  const isAdmin = () => {
    return user && user.role === "admin";
  };

  const value = {
    user,
    login,
    logout,
    forceLogout,
    isAuthenticated,
    isAdmin,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
